// BatiAssistDevisPage.jsx
// Page Devis Intelligents dans le contexte Espace Pro Abracadabati

import { motion } from 'framer-motion';
import { 
  FileText, CheckCircle, ArrowRight, Clock, 
  Calculator, Sparkles, Zap, Shield, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const features = [
  { icon: Clock, title: "Création rapide", desc: "Génère tes devis en quelques clics grâce aux modèles prédéfinis" },
  { icon: Calculator, title: "Calculs automatiques", desc: "TVA, marges, remises... tout est calculé automatiquement" },
  { icon: Sparkles, title: "Personnalisation", desc: "Adapte chaque devis avec ton logo et ta charte graphique" },
  { icon: Send, title: "Envoi direct", desc: "Envoie tes devis par email ou SMS en un clic" },
  { icon: Shield, title: "Signature électronique", desc: "Fais signer tes devis en ligne pour gagner du temps" },
  { icon: Zap, title: "Suivi en temps réel", desc: "Sache quand tes clients ouvrent et consultent tes devis" },
];

export default function BatiAssistDevisPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-devis-page">
        <UnifiedHeader />
        <EspaceProNav />
      
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <FileText className="w-4 h-4" />
                Devis Intelligents
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Crée tes devis en{' '}
                <span className="text-[#F97316]">quelques minutes</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10">
                Fini les heures passées sur Excel. Génère des devis professionnels en quelques clics, 
                personnalisés à ton image et prêts à être signés en ligne.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                  Découvrir les devis intelligents
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Tout ce dont tu as besoin pour tes <span className="text-[#F97316]">devis</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 bg-slate-50 rounded-2xl hover:shadow-lg transition-shadow"
                  >
                    <div className="w-14 h-14 rounded-xl bg-[#1E3A5F] flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F]">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prêt à créer des devis en un temps record ?
              </h2>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full px-10 py-6 text-lg">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
